import {
  CloseButton,
  Combobox,
  InputBase,
  ScrollArea,
  useCombobox,
} from '@mantine/core'
import React, { useMemo, useState } from 'react'

import { TextInput } from '@components/mantine/text-input.component'

type SelectItem = {
  value: string
  label: string
  disabled?: boolean
}

type RenderTriggerProps = {
  value: string | null | undefined
  selectedLabel: string | null
  toggle: () => void
  isOpen: boolean
  disabled: boolean
}

type SelectProps = {
  data: SelectItem[]
  value?: string | null
  onChange?: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  searchable?: boolean
  clearable?: boolean
  fullWidth?: boolean
  className?: string
  nothingFoundMessage?: string
  renderTrigger?: (props: RenderTriggerProps) => React.ReactNode
}

export function Select({
  data,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  searchable = false,
  clearable = false,
  fullWidth = false,
  className = '',
  nothingFoundMessage = 'No options found',
  renderTrigger,
}: SelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      setSearch('')
    },
    onDropdownOpen: () => {
      if (searchable) {
        combobox.focusSearchInput()
      }
    },
  })

  const [search, setSearch] = useState('')

  // Find the selected item's label
  const selectedLabel = useMemo(() => {
    const item = data.find(item => item.value === value)
    return item?.label || null
  }, [data, value])

  // Filter items based on search
  const filteredData = useMemo(() => {
    if (!search) return data

    const searchLower = search.toLowerCase().trim()
    return data.filter(item => item.label.toLowerCase().includes(searchLower))
  }, [data, search])

  const hasOptions = filteredData.length > 0

  const options = filteredData.map(item => (
    <Combobox.Option
      key={item.value}
      value={item.value}
      disabled={item.disabled}
      className="px-4 py-3 rounded-md cursor-pointer text-xl! data-[combobox-selected=true]:bg-grey-50 data-[combobox-active=true]:bg-grey-100"
    >
      {item.label}
    </Combobox.Option>
  ))

  return (
    <Combobox
      store={combobox}
      withinPortal
      portalProps={{ target: '#select-portal' }}
      position="bottom-start"
      offset={4}
      middlewares={{ flip: true, shift: true }}
      onOptionSubmit={val => {
        onChange?.(val)
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        {renderTrigger ? (
          renderTrigger({
            value,
            selectedLabel,
            toggle: () => combobox.toggleDropdown(),
            isOpen: combobox.dropdownOpened,
            disabled,
          })
        ) : (
          <InputBase
            component="button"
            type="button"
            pointer
            rightSection={
              clearable && value ? (
                <CloseButton
                  size="sm"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => onChange?.(null)}
                  aria-label="Clear selection"
                />
              ) : (
                <Combobox.Chevron />
              )
            }
            rightSectionPointerEvents={clearable && value ? 'all' : 'none'}
            onClick={() => combobox.toggleDropdown()}
            disabled={disabled}
            size="lg"
            classNames={{
              input: `form-input text-xl ${fullWidth ? 'w-full' : ''} ${className}`,
              wrapper: 'bg-transparent',
            }}
          >
            {selectedLabel || (
              <span className="text-grey-400 text-xl">{placeholder}</span>
            )}
          </InputBase>
        )}
      </Combobox.Target>

      <Combobox.Dropdown className="bg-white shadow-lg rounded-lg border border-grey-100 p-2">
        {searchable && (
          <div className="p-2">
            <TextInput
              value={search}
              onChange={setSearch}
              placeholder="Search..."
              autoFocus
            />
          </div>
        )}
        <ScrollArea.Autosize mah={300}>
          <Combobox.Options>
            {hasOptions ? (
              options
            ) : (
              <Combobox.Empty className="text-center py-4 text-grey-400">
                {nothingFoundMessage}
              </Combobox.Empty>
            )}
          </Combobox.Options>
        </ScrollArea.Autosize>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export type { SelectItem, RenderTriggerProps }
